import { Breadcrumb, BreadcrumbHint, Primitive } from '@sentry/core';
import { GqlResponseError } from 'utils/gql/errors/GqlResponseError';
import { INITIAL_INFO_NAME } from 'src/providers/User/AuthUserModels';
import { filterNotEmpty } from 'src/utils/data/listHelpers';
import { shouldIgnoreError } from './ignoredErrors';
import { bpeConfig } from '@enterprise/common';
import {
  init,
  setTags,
  setTag,
  browserTracingIntegration,
  captureConsoleIntegration,
  extraErrorDataIntegration,
  graphqlClientIntegration,
  reportingObserverIntegration,
  launchDarklyIntegration,
  captureException,
  setUser,
  captureMessage,
  replayIntegration,
  type User,
  type ErrorEvent,
  type EventHint,
  type BrowserOptions
} from '@sentry/react';
import { DEFAULT_TRACES_SAMPLE_RATE } from 'integrations/sentry/sampling/defaults';
import { readCache } from 'integrations/sentry/sampling/samplingCache';

export { setTag };

const traceExceptions = new RegExp(['TypeError', 'SyntaxError', 'RangeError'].join('|'));

enum EventType {
  ERROR = 'error',
  TRANSACTION = 'transaction',
  METRIC = 'metric'
}

const denyUrls = [
  // Google Adsense
  /pagead\/js/i,
  // Facebook flakiness
  /graph\.facebook\.com/i,
  // Facebook blocked
  /connect\.facebook\.net\/en_US\/all\.js/i,
  // Woopra flakiness
  /eatdifferent\.com\.woopra-ns\.com/i,
  /static\.woopra\.com\/js\/woopra\.js/i,
  // Chrome extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  // Other plugins
  /127\.0\.0\.1:4001\/isrunning/i, // Cacaoweb
  /webappstoolbarba\.texthelp\.com\//i,
  /metrics\.itunes\.apple\.com\.edgesuite\.net\//i
];
const DISABLE_SENTRY = process.env.DISABLE_SENTRY === 'true';

export function initSentry(
  isPublicFacing: boolean,
  tracesSampleRate = DEFAULT_TRACES_SAMPLE_RATE
): ReturnType<typeof init> {
  const dns = bpeConfig.integrations.sentryDns;
  const tenantId = bpeConfig.tenant.id;
  const release = bpeConfig.release;
  const cache = readCache();

  if (dns && !DISABLE_SENTRY) {
    const targetDns = dns.startsWith('https%3') ? decodeURIComponent(dns) : dns;
    setTags({
      namespace: tenantId,
      tenantCode: bpeConfig.tenant.code,
      tenantId: bpeConfig.tenant.id,
      environmentType: bpeConfig.environmentType
    });
    return init({
      ignoreErrors: ['[React Intl]'],
      // The following recommendations are from sntry's site:
      // https://gist.github.com/Chocksy/e9b2cdd4afc2aadc7989762c4b8b495a
      denyUrls,
      debug: process.env.NODE_ENV === 'development' && process.env.DEBUG === 'true',
      release,
      environment: tenantId,
      dsn: targetDns,
      integrations: filterNotEmpty([
        browserTracingIntegration({ enableInp: true }),
        captureConsoleIntegration({ levels: process.env.NODE_ENV !== 'development' ? ['error'] : [] }),
        extraErrorDataIntegration(),
        launchDarklyIntegration(),
        reportingObserverIntegration(),
        graphqlClientIntegration({
          endpoints: [/\/graphql$/]
        }),
        replayIntegration({
          maskAllText: true,
          blockAllMedia: true
        })
      ]),
      /* Note: The sampling decision is made before running processors, so if the transaction has been sampled out then the processor will not run. You may want to set o.TracesSampleRate = 1.0 when debugging.*/
      // Transaction sample rate
      tracesSampleRate: process.env.NODE_ENV === 'development' ? 1 : (cache?.value ?? tracesSampleRate),
      // Error sample rate
      sampleRate: 1,
      beforeBreadcrumb: attachOperationName,
      beforeSendTransaction,
      beforeSend: beforeSend(isPublicFacing),
      // Session Replay
      replaysSessionSampleRate: 0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
      replaysOnErrorSampleRate: !isPublicFacing ? 0.33 : 0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
    });
  }
  return undefined;
}

function isPublicPage(event: { transaction?: string }) {
  return event.transaction?.startsWith('/public.html');
}

function isLoggedInUser(event: { user?: User }) {
  return typeof event.user?.id === 'string' && event.user.id.startsWith('P');
}

const beforeSendTransaction: BrowserOptions['beforeSendTransaction'] = (event, hint) => {
  const isPublic = isPublicPage(event);
  if (isPublic || isLoggedInUser(event)) {
    event.tags = enrichTags(event.tags, EventType.TRANSACTION);
    return event;
  }
  return null;
};

const beforeSend = (isPublicFacing: boolean) => (event: ErrorEvent, hint: EventHint) => {
  const ignoreError = shouldIgnoreError(event, !isPublicFacing);

  if (ignoreError) {
    return null;
  }

  // Enrich the error event with tenant details
  event.tags = enrichTags(event.tags, EventType.ERROR);

  // if some js exceptions detected bump Severity to critical
  if (event?.exception?.values) {
    event?.exception?.values.forEach((value) => {
      if (traceExceptions.test(value.type ?? '')) {
        event.level = 'fatal';
        event.tags = { ...event.tags, alertLevel: 'Critical', type: 'javascript', kind: value.type };
      }
    });
  }
  // if get self fails bump Severity to critical
  if (event?.tags?.operationName === INITIAL_INFO_NAME) {
    event.level = 'fatal';
    event.tags = { ...event.tags, alertLevel: 'Critical', type: INITIAL_INFO_NAME };
  }
  // if 504 occurs bump Severity to critical
  if ((event?.extra as any)?.response?.status === 504) {
    event.level = 'fatal';
    event.tags = { ...event.tags, alertLevel: 'Critical', errorCode: '504' };
  }
  return event;
};

export function setSentryUser(user?: User) {
  setUser(user ?? null);
}

export function captureSentryException<E extends { response?: any } & Error>(
  error: E,
  operationName: string,
  extra?: object
) {
  captureException(error, {
    extra: { error, response: error.response, operationName, extra }
  });
}

export function captureSentryMessage(operationName: string, error?: GqlResponseError) {
  let extra;
  try {
    extra = error?.errors?.map((e) => ({
      extensions: JSON.stringify(e.extensions),
      locations: JSON.stringify(e.locations),
      message: e.message,
      path: JSON.stringify(e.path)
    }));
  } catch (e) {
    extra = error?.errors;
  }

  captureMessage(`graphql: ${operationName}`, {
    level: 'error',
    extra: { error: extra },
    tags: {
      errorCode: error?.errors?.[0]?.extensions?.code ?? 'UNKNOWN',
      type: 'graphql',
      operationName
    }
  });
}
export function attachOperationName(breadcrumb: Breadcrumb, hint?: BreadcrumbHint): Breadcrumb | null {
  if (breadcrumb.category === 'xhr' && breadcrumb.data?.url === '/graphql') {
    const operationName = getOperationName(JSON.parse(hint?.input)?.query);
    return { ...breadcrumb, data: { ...breadcrumb.data, url: `/graphql ${operationName}` } };
  }
  return breadcrumb;
}

function getOperationName(gqlQuery: string): string {
  try {
    return (
      gqlQuery
        .split(' ')
        .filter((el) => el)
        .filter((el) => el !== '\n')[1]
        ?.split('(')?.[0]
        ?.replace(/\W/g, '') ?? ''
    );
  } catch {
    return 'unknownGQLOperation';
  }
}

/**
 *  General tags with tenant code/id are set just before Sentry.init
 */
const enrichTags = (tags: { [key: string]: Primitive } | undefined, eventType: EventType) => {
  return {
    ...tags,
    eventType
  };
};

export function captureGqlUnusedFieldsMetric(operationName: string, unusedFields: string[]) {
  if (operationName === 'GetUseNotifications' || !unusedFields.length) {
    return;
  }
  captureMessage(`gql-unused-field: ${operationName}`, {
    tags: enrichTags({ metricSource: 'graphql', metricType: 'unused_fields', operationName }, EventType.METRIC),
    level: 'info',
    extra: {
      unusedFields
    }
  });
}
